import React from 'react';
import { Paper, Typography, Button, Box, List, ListItem, ListItemText, Divider, Chip } from '@mui/material';
import { useUser } from '@store';
import { useDriverSchedule } from '../hooks/useDriverSchedule';
import type { Drivers, Businesshours_Insert_Input } from '@gql/base-types';
import { ScheduleEditor } from '@components/Schedule';
import DriverOneOffDays from './DriverOneOffDays';
import { processDriverOneOffDays, formatTimeRange } from '../utils/driverScheduleUtils';
import { BiCalendarEvent } from 'react-icons/bi';
import dayjs from 'dayjs';
import { getDriverTimezone } from '@utils/timezoneUtils';

/** Only show one-off days from today onward + last 7 days */
const PAST_DISPLAY_DAYS = 7;

interface DriverScheduleProps {
  driver: Drivers;
}

const DriverSchedule: React.FC<DriverScheduleProps> = ({ driver }) => {
  const user = useUser();
  const driverId = driver?.id?.toString();
  const timezone = getDriverTimezone(driver);
  const userName = user?.profile?.display_name || 'Unknown';

  const [oneOffModalOpen, setOneOffModalOpen] = React.useState(false);

  const { driverSchedule, createDriverSchedule, updateDriverSchedule, deleteDriverSchedule, loading, refetch } =
    useDriverSchedule(driverId);

  const handleCreate = async (schedules: Businesshours_Insert_Input[]) => {
    await createDriverSchedule({ variables: { driverSchedule: schedules } });
  };

  // Compute current-week overrides for the schedule grid
  const currentWeekOverrides = React.useMemo(() => {
    const weekStart = dayjs().startOf('week');
    const weekEnd = dayjs().endOf('week');

    const oneOffThisWeek = driverSchedule.filter((h) => {
      if (h.driver_id?.toString() !== driverId) return false;
      if (h.type !== 'one-off') return false;
      if (!h.day || !h.month || !h.year) return false;
      const date = dayjs(new Date(h.year, h.month - 1, h.day));
      return !date.isBefore(weekStart, 'day') && !date.isAfter(weekEnd, 'day');
    });

    return oneOffThisWeek.map((h) => {
      const date = dayjs(new Date(h.year!, h.month! - 1, h.day!));
      return {
        dayOfWeek: date.day(),
        date: date.format('MMMM D'),
        name: h.name || 'Override',
        isAvailable: !!h.is_open,
        openTime: h.open_time,
        closedTime: h.closed_time,
        formattedTimeRange:
          h.open_time && h.closed_time
            ? formatTimeRange(h.open_time, h.closed_time, timezone)
            : '',
      };
    });
  }, [driverSchedule, driverId, timezone]);

  // Process one-off days — only show future + recent past
  const allOneOffDays = processDriverOneOffDays(driverSchedule, driverId!);
  const cutoff = dayjs().subtract(PAST_DISPLAY_DAYS, 'day').startOf('day');
  const visibleOneOffDays = allOneOffDays.filter((day) => dayjs(day.dateObj).isAfter(cutoff));
  const today = dayjs().startOf('day');
  const upcomingOneOffDays = visibleOneOffDays.filter((day) => !dayjs(day.dateObj).isBefore(today));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Weekly Schedule */}
      <ScheduleEditor
        config={{
          entityType: 'driver',
          entityId: driverId!,
          timezone: timezone,
          labels: {
            title: 'Weekly Availability',
            closed: 'Unavailable',
            openLabel: 'Available From',
            closeLabel: 'Available Until',
            unavailableText: (dayName) => `Driver is unavailable on ${dayName}s`,
          },
          types: {
            specific: 'business-hours',
            default: 'driver-default-hours',
          },
          naming: (dayName) => `Driver Schedule - ${dayName}`,
        }}
        scheduleData={driverSchedule}
        onCreate={handleCreate}
        onUpdate={updateDriverSchedule}
        onDelete={deleteDriverSchedule}
        loading={loading}
        userName={userName}
        currentWeekOverrides={currentWeekOverrides}
        onRefetch={() => refetch()}
        onManageOverrides={() => setOneOffModalOpen(true)}
      />

      {/* One-Off Days Section */}
      <Paper sx={{ p: 3, borderRadius: '12px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>Schedule Overrides</Typography>
            <Typography variant="body2" color="text.secondary">
              One-off exceptions to the weekly schedule
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<BiCalendarEvent />}
            onClick={() => setOneOffModalOpen(true)}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Manage Overrides
          </Button>
        </Box>

        {upcomingOneOffDays.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No upcoming overrides scheduled
          </Typography>
        ) : (
          <List sx={{ pt: 0, border: '1px solid', borderColor: 'divider', borderRadius: '8px' }}>
            {upcomingOneOffDays.slice(0, 5).map((day, index) => (
              <React.Fragment key={day.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ py: 1 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {day.name || 'Override'}
                        </Typography>
                        <Chip
                          label={day.isWorking ? 'Working' : 'Off'}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: 11,
                            bgcolor: day.isWorking ? '#f3fbf4' : '#fff2f4',
                            color: day.isWorking ? '#20b020' : '#f02020',
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <>
                        {day.date}
                        {day.openTime && day.closedTime && (
                          <Typography component="span" variant="caption" display="block" color="text.secondary">
                            {day.isWorking ? 'Available' : 'Unavailable'}: {day.openTime} – {day.closedTime}
                          </Typography>
                        )}
                      </>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
        {upcomingOneOffDays.length > 5 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            And {upcomingOneOffDays.length - 5} more...
          </Typography>
        )}
      </Paper>

      {/* One-Off Days Modal */}
      <DriverOneOffDays driver={driver} open={oneOffModalOpen} onClose={() => setOneOffModalOpen(false)} />
    </Box>
  );
};

export default DriverSchedule;
